// ─── Imperium Design Tokens ───────────────────────────────────────────────────
// Phylax-inspired dark system: negro dominante, dorado como acento

export const IMPERIUM_PALETTE = {
  brand: {
    // Dorado — acento principal
    golden:        '#D8BF82',
    goldenLight:   '#EDD9A8',
    goldenMid:     '#B8956F',
    goldenDark:    '#9A7A5C',
    goldenSubtle:  'rgba(216,191,130,0.10)',
    goldenBorder:  'rgba(216,191,130,0.20)',
    goldenGlow:    'rgba(216,191,130,0.15)',
    loginTint:     '#1C1509',   // tinte del panel del login
    onAccent:      '#1A1A1A',   // texto/íconos SOBRE el acento (dorado → negro)
    accentCyan:    '#38BDF8',   // acento secundario (glow); Imperium no lo usa

    // Negro — base dominante
    primary:       '#222222',
    primaryLight:  '#2E2E2E',
    primaryDark:   '#0A0A0A',
  },

  // Backgrounds oscuros (igual jerarquía que Phylax)
  bg: {
    base:      '#0A0A0A',   // fondo raíz
    paper:     '#141414',   // cards, drawers
    elevated:  '#1A1A1A',   // popovers, menús
    overlay:   '#202020',   // hover states ligeros
    glass:     'rgba(20,20,20,0.80)', // glassmorphism
  },

  // Texto
  text: {
    primary:   '#F0EDE6',   // ligeramente cálido (no frío)
    secondary: '#9C9487',   // dorado desaturado
    disabled:  '#5C5650',
    inverse:   '#0A0A0A',
  },

  // Bordes
  border: {
    subtle:  'rgba(255,255,255,0.06)',
    default: 'rgba(255,255,255,0.10)',
    strong:  'rgba(255,255,255,0.18)',
    golden:  'rgba(216,191,130,0.25)',
  },

  // Sombras negras profundas (igual que Phylax)
  shadow: {
    sm:  '0 1px 3px rgba(0,0,0,0.4)',
    md:  '0 4px 16px rgba(0,0,0,0.5)',
    lg:  '0 8px 32px rgba(0,0,0,0.6)',
    xl:  '0 16px 48px rgba(0,0,0,0.7)',
    glow:'0 0 20px rgba(216,191,130,0.12)',
  },

  gradients: {
    golden:     'linear-gradient(135deg, #D8BF82 0%, #B8956F 100%)',
    goldenSoft: 'linear-gradient(135deg, #EDD9A8 0%, #D8BF82 100%)',
    darkGolden: 'linear-gradient(135deg, #B8956F 0%, #9A7A5C 100%)',
    loginBg:    'linear-gradient(160deg, #1A1A1A 0%, #0A0A0A 60%, #1C1509 100%)',
    cardSheen:  'linear-gradient(135deg, rgba(216,191,130,0.05) 0%, transparent 60%)',
  },

  // Mantener grey para compat con MUI
  grey: {
    50:  '#F9F9F9', 100: '#F0EDE6', 200: '#D9D4CC',
    300: '#B8B0A4', 400: '#9C9487', 500: '#7A7268',
    600: '#5C5650', 700: '#3A3530', 800: '#222018', 900: '#0A0A0A',
  },
};

// ─── Booster — CELESTE ────────────────────────────────────────────────────────
// Misma base oscura que Imperium (negro dominante); lo único que cambia es el acento.
// Mantiene la MISMA forma de objeto que IMPERIUM_PALETTE para que buildTheme y los
// componentes funcionen sin cambios.
export const BOOSTER_PALETTE = {
  ...IMPERIUM_PALETTE,
  brand: {
    /**
     * 25/08/2026 — el acento de Booster pasa del cian al CELESTE.
     *
     * El celeste entró como conmemoración del 25 de Agosto en el CRM de Imperium, se vio
     * puertas adentro y afuera durante un día entero, y se decidió que es el color de
     * Booster. NO es una variante temporal: acá es permanente y no depende de ninguna
     * fecha. La conmemoración sigue siendo cosa aparte (`conmemoracion.ts`) y solo aplica
     * al tenant de Imperium, que el resto del año es dorado.
     *
     * Es el MISMO tono que ya se validó: `#75AADB` es `CELESTE_URUGUAY`, y la escala son
     * exactamente los valores que deriva `withAccent()` a partir de él — los que se vieron
     * en pantalla. Escritos a mano y no derivados porque un tenant SIN color propio nunca
     * pasa por `withAccent`: lo que se ve es literalmente este objeto.
     *
     * ⚠️ Esto es el DEFAULT de la marca: lo ven los tenants de Booster SIN color propio y
     * las pantallas pre-login. Un tenant con `branding.primaryColor` sigue con el suyo.
     *
     * Los valores viejos, por si hay que volver:
     *   cian     #22D3EE · #67E8F9 · #0891B2 · #0E5C6E · loginTint #061019 · onAccent #03222B
     *   emerald  #059669 · #34d399 · #047857 · #065f46 · loginTint #04130D
     */
    ...IMPERIUM_PALETTE.brand,
    golden:        '#75AADB',   // celeste — acento principal
    goldenLight:   '#9BC1E5',   // highlight
    goldenMid:     '#6695C0',   // hover / secondary
    /**
     * ⚠️ EL TONO DEL MODO CLARO. `applyCrmMode` lo usa como `brand.golden` cuando el CRM
     * está en claro, así que es el que pinta texto e íconos sobre fondo blanco. Da 4.52:1
     * contra el blanco: pasa el mínimo AA (4.5) raspando. Cualquier tono más claro que este
     * deja de leerse — el celeste de la bandera solo da 2.46:1 y por eso no puede ir acá.
     */
    goldenDark:    '#547A9D',
    goldenSubtle:  'rgba(117,170,219,0.10)',
    goldenBorder:  'rgba(117,170,219,0.25)',
    goldenGlow:    'rgba(117,170,219,0.18)',
    /**
     * ⛔ NO es el derivado de `withAccent` (`#2C4053`), a diferencia del resto de la escala.
     * `loginTint` es AMBIENTE —el fondo del panel del login, que arranca acá y va a negro—,
     * y ese derivado sale demasiado claro para eso: se lee como un panel gris azulado en vez
     * de como noche. Imperium usa `#1C1509`, el cian usaba `#061019`; este es el mismo
     * escalón de oscuridad en el hue del celeste.
     */
    loginTint:     '#0B1420',
    /**
     * ⛔ NEGRO, no blanco. El celeste es un tono MEDIO y cae justo del lado equivocado del
     * umbral de luminancia: blanco encima da 2.46:1 (ilegible), negro da 7.07:1. Es el
     * texto de los botones primarios, así que la diferencia se ve en cada pantalla.
     * `withAccent` decide esto solo y bien (ver la nota de `onAccent` ahí).
     */
    onAccent:      '#1A1A1A',
    accentCyan:    '#8DB9E1',   // secundario = celeste claro
  },
  border: {
    ...IMPERIUM_PALETTE.border,
    golden: 'rgba(117,170,219,0.28)',
  },
  shadow: {
    ...IMPERIUM_PALETTE.shadow,
    glow: '0 0 20px rgba(117,170,219,0.16)',
  },
  gradients: {
    ...IMPERIUM_PALETTE.gradients,
    // Los GRADIENTES van con `brand.*`: son los que pintan el botón primario, la línea
    // del eyebrow del login y el sheen de las tarjetas. Cambiar solo los colores planos
    // dejaba media pantalla del color anterior. (20/08/2026)
    golden:     'linear-gradient(135deg, #75AADB 0%, #6695C0 100%)',
    goldenSoft: 'linear-gradient(135deg, #9BC1E5 0%, #75AADB 100%)',
    darkGolden: 'linear-gradient(135deg, #6695C0 0%, #547A9D 100%)',
    loginBg:    'linear-gradient(160deg, #0B1420 0%, #0A0A0A 60%, #0B1420 100%)',
    cardSheen:  'linear-gradient(135deg, rgba(117,170,219,0.05) 0%, transparent 60%)',
  },
};

// ─── Booster — variante AZUL (PRUEBA, tipo Phylax: familia sky/blue) ───────────
// Acento azul Tailwind blue-500 (#3B82F6), como los acentos de Phylax. Sobre el
// azul el texto va BLANCO (onAccent), a diferencia del dorado/lime que va oscuro.
export const BOOSTER_BLUE_PALETTE = {
  ...IMPERIUM_PALETTE,
  brand: {
    ...IMPERIUM_PALETTE.brand,
    golden:        '#3B82F6',   // azul — acento principal
    goldenLight:   '#60A5FA',   // azul claro (highlights)
    goldenMid:     '#2563EB',   // azul medio (secondary, hover)
    goldenDark:    '#1D4ED8',   // azul oscuro (bordes, estados)
    goldenSubtle:  'rgba(59,130,246,0.12)',
    goldenBorder:  'rgba(59,130,246,0.25)',
    goldenGlow:    'rgba(59,130,246,0.18)',
    loginTint:     '#0A1020',   // tinte azulado del panel del login
    onAccent:      '#FFFFFF',   // texto SOBRE el azul → blanco
  },
  border: {
    ...IMPERIUM_PALETTE.border,
    golden: 'rgba(59,130,246,0.28)',
  },
  shadow: {
    ...IMPERIUM_PALETTE.shadow,
    glow: '0 0 20px rgba(59,130,246,0.14)',
  },
  gradients: {
    ...IMPERIUM_PALETTE.gradients,
    golden:     'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
    goldenSoft: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
    darkGolden: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
    loginBg:    'linear-gradient(160deg, #0A1020 0%, #0A0A0A 60%, #0B1224 100%)',
    cardSheen:  'linear-gradient(135deg, rgba(59,130,246,0.05) 0%, transparent 60%)',
  },
};
